// src/utils/tourTemplates.ts

import { ScheduleItem, EVENT_TYPES, RESIDENCY_EVENT_TYPES } from './scheduleTemplates';
import { 
  TourVariables, 
  TourDay, 
  TourDayType, 
  TransportType, 
  MealType,
  TourTemplate,
  TourGenerationOptions 
} from '../types/Tour';
import { addDays, format } from 'date-fns';

// Tour-specific event types (extends existing EVENT_TYPES)
export const TOUR_EVENT_TYPES = {
  VENUE_ARRIVAL: 'ARRIVAL',
  TRAVEL_DAY: 'TRAVEL',
  MERCHANDISE_SETUP: 'OTHER',
  TOUR_MEETING: 'MEETING',
  CITY_DEPARTURE: 'TRAVEL',
  CITY_ARRIVAL: 'ARRIVAL',
  HOTEL_CHECKIN: 'ACCOMMODATION',
  HOTEL_CHECKOUT: 'ACCOMMODATION',
  VENUE_TO_HOTEL: 'TRAVEL',
  HOTEL_TO_VENUE: 'TRAVEL'
} as const;

// Default tour generation options
const DEFAULT_TOUR_OPTIONS: TourGenerationOptions = {
  includeRestDays: false,
  includeLoadInDays: false,
  autoCalculateTravel: true,
  maxTravelHoursPerDay: 6,
  
  defaultMealTimes: {
    breakfast: '08:00',
    lunch: '13:00',
    dinner: '19:00'
  },
  
  defaultSoundcheckDuration: 60,
  defaultPerformanceDuration: 90,
  defaultLoadInDuration: 120
};

/**
 * Helper function to get route-specific transport type between venues
 */
const getRouteTransportType = (
  fromVenue: number, 
  toVenue: number, 
  variables: TourVariables
): string => {
  const routeKey = `VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE`;
  return (variables as any)[routeKey] || 'private_car';
};

/**
 * Helper function to get route-specific departure time
 */
const getRouteDepartureTime = (
  fromVenue: number, 
  toVenue: number, 
  variables: TourVariables
): string => {
  const routeKey = `VENUE_${fromVenue}_TO_VENUE_${toVenue}_DEPARTURE_TIME`;
  return (variables as any)[routeKey] || '12:00';
};

/**
 * Helper function to get route-specific arrival time
 */
const getRouteArrivalTime = (
  fromVenue: number, 
  toVenue: number, 
  variables: TourVariables
): string => {
  const routeKey = `VENUE_${fromVenue}_TO_VENUE_${toVenue}_ARRIVAL_TIME`;
  return (variables as any)[routeKey] || '15:00';
};

/**
 * Helper function to get route-specific booking details
 */
const getRouteDetails = (
  fromVenue: number, 
  toVenue: number, 
  variables: TourVariables
): string => {
  const routeKey = `VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS`;
  return (variables as any)[routeKey] || '';
};

/**
 * Helper function to get venue-specific field value
 */
const getVenueField = (venueNumber: number, field: string, variables: TourVariables): string => {
  const venueKey = `VENUE_${venueNumber}_${field}`;
  return (variables as any)[venueKey] || '';
};

/**
 * Determines if route requires overnight travel based on departure/arrival times
 */
const requiresOvernightTravel = (
  fromVenue: number,
  toVenue: number,
  variables: TourVariables
): boolean => {
  const departureTime = getRouteDepartureTime(fromVenue, toVenue, variables);
  const arrivalTime = getRouteArrivalTime(fromVenue, toVenue, variables);
  
  // Parse times (assuming HH:MM format)
  const [depHour, depMin] = departureTime.split(':').map(Number);
  const [arrHour, arrMin] = arrivalTime.split(':').map(Number);
  
  const departureMinutes = depHour * 60 + depMin;
  const arrivalMinutes = arrHour * 60 + arrMin;
  
  // If arrival is next day OR travel time > 6 hours, requires overnight
  const travelMinutes = arrivalMinutes >= departureMinutes 
    ? arrivalMinutes - departureMinutes
    : (24 * 60) - departureMinutes + arrivalMinutes;
    
  return travelMinutes > (6 * 60); // More than 6 hours
};

/**
 * Creates hotel check-in item with venue-specific hotel variables
 */
const createHotelCheckInItem = (
  id: string,
  time: string,
  venueNumber: number,
  variables: TourVariables
): ScheduleItem => {
  const hotelName = getVenueField(venueNumber, 'HOTEL_NAME', variables);
  const hotelContact = getVenueField(venueNumber, 'HOTEL_CONTACT_NAME', variables);
  const hotelContactPhone = getVenueField(venueNumber, 'HOTEL_CONTACT_PHONE', variables);
  
  let description = 'Check-in to hotel';
  if (hotelName) {
    description = `Check-in to {VENUE_${venueNumber}_HOTEL_NAME}`;
  }
  if (hotelContact) {
    description += ` (Contact: {VENUE_${venueNumber}_HOTEL_CONTACT_NAME}`;
    if (hotelContactPhone) {
      description += ` - {VENUE_${venueNumber}_HOTEL_CONTACT_PHONE}`;
    }
    description += ')';
  }
  
  return {
    id,
    time,
    type: 'ACCOMMODATION',
    description,
    template: description
  };
};

/**
 * Creates hotel check-out item with venue-specific hotel variables
 */
const createHotelCheckOutItem = (
  id: string,
  time: string,
  venueNumber: number,
  variables: TourVariables
): ScheduleItem => {
  const hotelName = getVenueField(venueNumber, 'HOTEL_NAME', variables);
  
  let description = 'Hotel checkout';
  if (hotelName) {
    description = `Checkout from {VENUE_${venueNumber}_HOTEL_NAME}`;
  }
  
  return {
    id,
    time,
    type: 'ACCOMMODATION',
    description,
    template: description
  };
};

/**
 * Main tour template generation function
 */
export const getTourTemplate = (
  variables: TourVariables, 
  options: Partial<TourGenerationOptions> = {}
): TourDay[] => {
  const opts = { ...DEFAULT_TOUR_OPTIONS, ...options };
  
  const numberOfShows = parseInt(variables.NUMBER_OF_SHOWS) || 0;
  if (numberOfShows === 0) return [];
  
  return generateTourDays(variables, numberOfShows, opts);
};

/**
 * Core function that orchestrates the entire tour timeline
 */
const generateTourDays = (
  variables: TourVariables,
  numberOfShows: number,
  options: TourGenerationOptions
): TourDay[] => {
  const days: TourDay[] = [];
  let currentDate = new Date(variables.TOUR_START_DATE);
  let dayNumber = 1;

  for (let showNumber = 1; showNumber <= numberOfShows; showNumber++) {
    const isFirstShow = showNumber === 1;
    const isLastShow = showNumber === numberOfShows;
    
    if (!isFirstShow) {
      // Check if we need overnight travel from previous venue
      const fromVenue = showNumber - 1;
      const toVenue = showNumber;
      
      if (requiresOvernightTravel(fromVenue, toVenue, variables)) {
        // Create travel-only day
        const fromCity = getVenueField(fromVenue, 'CITY', variables);
        const toCity = getVenueField(toVenue, 'CITY', variables);
        
        const travelDay = createTravelOnlyDay(
          fromCity, toCity, currentDate, variables, dayNumber, fromVenue, toVenue
        );
        days.push(travelDay);
        
        currentDate = addDays(currentDate, 1);
        dayNumber++;
      }
    }
    
    // Create performance day (with or without travel)
    const performanceDay = isFirstShow 
      ? createFirstPerformanceDay(currentDate, variables, dayNumber, showNumber)
      : createTravelPerformanceDay(currentDate, variables, dayNumber, showNumber);
      
    days.push(performanceDay);
    
    currentDate = addDays(currentDate, 1);
    dayNumber++;
  }
  
  return days;
};

/**
 * Creates the first performance day (no incoming travel)
 */
const createFirstPerformanceDay = (
  date: Date,
  variables: TourVariables,
  dayNumber: number,
  venueNumber: number
): TourDay => {
  const venueCity = getVenueField(venueNumber, 'CITY', variables);
  const venueName = getVenueField(venueNumber, 'NAME', variables);
  const performanceTime = getVenueField(venueNumber, 'PERFORMANCE_TIME', variables);
  const soundcheckTime = getVenueField(venueNumber, 'SOUNDCHECK_TIME', variables);
  
  const items: ScheduleItem[] = [
    {
      id: `first-day-${dayNumber}-1`,
      time: '08:00',
      type: 'CATERING',
      description: `Breakfast at {VENUE_${venueNumber}_HOTEL_NAME}`
    },
    {
      id: `first-day-${dayNumber}-2`,
      time: '12:00',
      type: 'TRAVEL',
      description: `Travel from hotel to {VENUE_${venueNumber}_NAME}`,
      template: `Travel from hotel to {VENUE_${venueNumber}_NAME}`
    },
    {
      id: `first-day-${dayNumber}-3`,
      time: '13:00',
      type: 'ARRIVAL',
      description: `Arrive at {VENUE_${venueNumber}_NAME}, meet {VENUE_${venueNumber}_PRIMARY_CONTACT_NAME}`,
      template: `Arrive at {VENUE_${venueNumber}_NAME}, meet {VENUE_${venueNumber}_PRIMARY_CONTACT_NAME}`
    },
    {
      id: `first-day-${dayNumber}-4`,
      time: '14:00',
      type: 'VENUE',
      description: `Load-in and technical setup with {VENUE_${venueNumber}_TECH_CONTACT_NAME}`,
      template: `Load-in and technical setup with {VENUE_${venueNumber}_TECH_CONTACT_NAME}`
    }
  ];
  
  // Add soundcheck if time specified
  if (soundcheckTime) {
    items.push({
      id: `first-day-${dayNumber}-5`,
      time: soundcheckTime,
      type: 'SOUNDCHECK',
      description: `Soundcheck at {VENUE_${venueNumber}_NAME}`,
      template: `Soundcheck at {VENUE_${venueNumber}_NAME}`
    });
  }
  
  // Add pre-show meal
  items.push({
    id: `first-day-${dayNumber}-6`,
    time: '17:30',
    type: 'CATERING',
    description: getVenueMealDescription(venueNumber, 'DINNER', variables)
  });
  
  // Add performance
  if (performanceTime) {
    items.push({
      id: `first-day-${dayNumber}-7`,
      time: performanceTime,
      type: 'PERFORMANCE',
      description: `Performance begins at {VENUE_${venueNumber}_NAME}`,
      template: `Performance begins at {VENUE_${venueNumber}_NAME}`
    });
  }
  
  // Add load-out and return to hotel
  items.push(
    {
      id: `first-day-${dayNumber}-8`,
      time: '22:30',
      type: 'LOADOUT',
      description: `Performance ends, load-out begins`
    },
    {
      id: `first-day-${dayNumber}-9`,
      time: '23:30',
      type: 'TRAVEL',
      description: `Return to {VENUE_${venueNumber}_HOTEL_NAME}`,
      template: `Return to {VENUE_${venueNumber}_HOTEL_NAME}`
    }
  );
  
  return {
    dayNumber,
    date,
    dayType: TourDayType.PERFORMANCE_ONLY,
    location: venueCity,
    venue: {
      venueNumber: venueNumber,
      name: venueName,
      city: venueCity,
      address: getVenueField(venueNumber, 'ADDRESS', variables),
      performanceDate: getVenueField(venueNumber, 'PERFORMANCE_DATE', variables),
      performanceTime: getVenueField(venueNumber, 'PERFORMANCE_TIME', variables),
      primaryContact: {
        name: getVenueField(venueNumber, 'PRIMARY_CONTACT_NAME', variables),
        phone: getVenueField(venueNumber, 'PRIMARY_CONTACT_PHONE', variables)
      },
      technicalContact: {
        name: getVenueField(venueNumber, 'TECH_CONTACT_NAME', variables),
        phone: getVenueField(venueNumber, 'TECH_CONTACT_PHONE', variables)
      },
      lunchType: getVenueField(venueNumber, 'LUNCH_TYPE', variables) as MealType,
      dinnerType: getVenueField(venueNumber, 'DINNER_TYPE', variables) as MealType
    },
    items
  };
};

/**
 * Creates a combined travel + performance day
 */
const createTravelPerformanceDay = (
  date: Date,
  variables: TourVariables,
  dayNumber: number,
  venueNumber: number
): TourDay => {
  const fromVenue = venueNumber - 1;
  const toVenue = venueNumber;
  
  const fromCity = getVenueField(fromVenue, 'CITY', variables);
  const toCity = getVenueField(toVenue, 'CITY', variables);
  const venueName = getVenueField(toVenue, 'NAME', variables);
  
  const transportType = getRouteTransportType(fromVenue, toVenue, variables);
  const departureTime = getRouteDepartureTime(fromVenue, toVenue, variables);
  const arrivalTime = getRouteArrivalTime(fromVenue, toVenue, variables);
  const routeDetails = getRouteDetails(fromVenue, toVenue, variables);
  
  const performanceTime = getVenueField(toVenue, 'PERFORMANCE_TIME', variables);
  const soundcheckTime = getVenueField(toVenue, 'SOUNDCHECK_TIME', variables);
  
  const items: ScheduleItem[] = [
    {
      id: `travel-perf-${dayNumber}-1`,
      time: '08:00',
      type: 'CATERING',
      description: `Breakfast at {VENUE_${fromVenue}_HOTEL_NAME} in ${fromCity}`
    },
    createHotelCheckOutItem(
      `travel-perf-${dayNumber}-2`,
      variables.DEFAULT_CHECK_OUT_TIME || '11:00',
      fromVenue,
      variables
    ),
    {
      id: `travel-perf-${dayNumber}-3`,
      time: departureTime,
      type: 'TRAVEL',
      description: `Travel from ${fromCity} to ${toCity} via {VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE}${routeDetails ? ` - {VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS}` : ''}`,
      template: `Travel from ${fromCity} to ${toCity} via {VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE}${routeDetails ? ` - {VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS}` : ''}`
    },
    {
      id: `travel-perf-${dayNumber}-4`,
      time: arrivalTime,
      type: 'ARRIVAL',
      description: `Arrive in ${toCity}`
    },
    createHotelCheckInItem(
      `travel-perf-${dayNumber}-5`,
      getTimeAfter(arrivalTime, 60),
      toVenue,
      variables
    ),
    {
      id: `travel-perf-${dayNumber}-6`,
      time: getTimeAfter(arrivalTime, 120),
      type: 'TRAVEL',
      description: `Travel to {VENUE_${toVenue}_NAME}`,
      template: `Travel to {VENUE_${toVenue}_NAME}`
    },
    {
      id: `travel-perf-${dayNumber}-7`,
      time: getTimeAfter(arrivalTime, 150),
      type: 'ARRIVAL',
      description: `Arrive at {VENUE_${toVenue}_NAME}, meet {VENUE_${toVenue}_PRIMARY_CONTACT_NAME}`,
      template: `Arrive at {VENUE_${toVenue}_NAME}, meet {VENUE_${toVenue}_PRIMARY_CONTACT_NAME}`
    }
  ];
  
  // Add soundcheck if specified
  if (soundcheckTime) {
    items.push({
      id: `travel-perf-${dayNumber}-8`,
      time: soundcheckTime,
      type: 'SOUNDCHECK',
      description: `Soundcheck with {VENUE_${toVenue}_TECH_CONTACT_NAME}`,
      template: `Soundcheck with {VENUE_${toVenue}_TECH_CONTACT_NAME}`
    });
  }
  
  // Add pre-show meal
  items.push({
    id: `travel-perf-${dayNumber}-9`,
    time: '17:30',
    type: 'CATERING',
    description: getVenueMealDescription(toVenue, 'DINNER', variables)
  });
  
  // Add performance
  if (performanceTime) {
    items.push({
      id: `travel-perf-${dayNumber}-10`,
      time: performanceTime,
      type: 'PERFORMANCE',
      description: `Performance begins at {VENUE_${toVenue}_NAME}`,
      template: `Performance begins at {VENUE_${toVenue}_NAME}`
    });
  }
  
  // Add load-out and return to hotel
  items.push(
    {
      id: `travel-perf-${dayNumber}-11`,
      time: '22:30',
      type: 'LOADOUT',
      description: `Performance ends, load-out begins`
    },
    {
      id: `travel-perf-${dayNumber}-12`,
      time: '23:30',
      type: 'TRAVEL',
      description: `Return to {VENUE_${toVenue}_HOTEL_NAME}`,
      template: `Return to {VENUE_${toVenue}_HOTEL_NAME}`
    }
  );
  
  return {
    dayNumber,
    date,
    dayType: TourDayType.TRAVEL_PERFORMANCE,
    location: `${fromCity} → ${toCity}`,
    venue: {
      venueNumber: toVenue,
      name: venueName,
      city: toCity,
      address: getVenueField(toVenue, 'ADDRESS', variables),
      performanceDate: getVenueField(toVenue, 'PERFORMANCE_DATE', variables),
      performanceTime: getVenueField(toVenue, 'PERFORMANCE_TIME', variables),
      primaryContact: {
        name: getVenueField(toVenue, 'PRIMARY_CONTACT_NAME', variables),
        phone: getVenueField(toVenue, 'PRIMARY_CONTACT_PHONE', variables)
      },
      technicalContact: {
        name: getVenueField(toVenue, 'TECH_CONTACT_NAME', variables),
        phone: getVenueField(toVenue, 'TECH_CONTACT_PHONE', variables)
      },
      lunchType: getVenueField(venueNumber, 'LUNCH_TYPE', variables) as MealType,
      dinnerType: getVenueField(venueNumber, 'DINNER_TYPE', variables) as MealType
    },
    items,
    travelInfo: {
      fromCity,
      toCity,
      transportType: transportType as TransportType,
      departureTime,
      arrivalTime
    }
  };
};

/**
 * Creates a travel-only day for long distances
 */
const createTravelOnlyDay = (
  fromCity: string,
  toCity: string,
  date: Date,
  variables: TourVariables,
  dayNumber: number,
  fromVenue: number,
  toVenue: number
): TourDay => {
  const transportType = getRouteTransportType(fromVenue, toVenue, variables);
  const departureTime = getRouteDepartureTime(fromVenue, toVenue, variables);
  const arrivalTime = getRouteArrivalTime(fromVenue, toVenue, variables);
  const routeDetails = getRouteDetails(fromVenue, toVenue, variables);
  
  const items: ScheduleItem[] = [
    {
      id: `travel-only-${dayNumber}-1`,
      time: '08:00',
      type: 'CATERING',
      description: `Breakfast at {VENUE_${fromVenue}_HOTEL_NAME} in ${fromCity}`
    },
    createHotelCheckOutItem(
      `travel-only-${dayNumber}-2`,
      variables.DEFAULT_CHECK_OUT_TIME || '11:00',
      fromVenue,
      variables
    ),
    {
      id: `travel-only-${dayNumber}-3`,
      time: departureTime,
      type: 'TRAVEL',
      description: `Long-distance travel from ${fromCity} to ${toCity} via {VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE}${routeDetails ? ` - {VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS}` : ''}`,
      template: `Long-distance travel from ${fromCity} to ${toCity} via {VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE}${routeDetails ? ` - {VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS}` : ''}`
    },
    {
      id: `travel-only-${dayNumber}-4`,
      time: arrivalTime,
      type: 'ARRIVAL',
      description: `Arrive in ${toCity}`
    },
    createHotelCheckInItem(
      `travel-only-${dayNumber}-5`,
      getTimeAfter(arrivalTime, 60),
      toVenue,
      variables
    ),
    {
      id: `travel-only-${dayNumber}-6`,
      time: '20:00',
      type: 'CATERING',
      description: 'Dinner and rest evening before tomorrow\'s show'
    }
  ];
  
  return {
    dayNumber,
    date,
    dayType: TourDayType.TRAVEL_ONLY,
    location: `${fromCity} → ${toCity}`,
    items,
    travelInfo: {
      fromCity,
      toCity,
      transportType: transportType as TransportType,
      departureTime,
      arrivalTime
    }
  };
};

/**
 * Helper to get meal description with venue-specific variables
 */
const getVenueMealDescription = (
  venueNumber: number,
  mealType: 'LUNCH' | 'DINNER',
  variables: TourVariables
): string => {
  const mealTypeField = getVenueField(venueNumber, `${mealType}_TYPE`, variables);
  const mealLocation = getVenueField(venueNumber, `${mealType}_LOCATION`, variables);
  
  if (mealTypeField === 'venue_catering') {
    return `${mealType.toLowerCase()} - venue catering${mealLocation ? ` at {VENUE_${venueNumber}_${mealType}_LOCATION}` : ''}`;
  } else if (mealTypeField === 'restaurant') {
    return `${mealType.toLowerCase()} at {VENUE_${venueNumber}_${mealType}_LOCATION}`;
  } else if (mealTypeField === 'per_diem') {
    return `${mealType.toLowerCase()} - per diem allowance {TOUR_DAILY_ALLOWANCE}`;
  }
  
  return `Pre-show ${mealType.toLowerCase()}`;
};

/**
 * Helper to add minutes to a time string (HH:MM format)
 */
const getTimeAfter = (timeStr: string, addMinutes: number): string => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + addMinutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  
  return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
};

/**
 * Apply tour variables to schedule items (for variable substitution)
 */
export const applyTourVariables = (template: string, variables: TourVariables): string => {
  let result = template;
  
  // Replace all variable placeholders with actual values
  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    result = result.replace(new RegExp(placeholder, 'g'), String(value || ''));
  });
  
  return result;
};

/**
 * Get available tour event types for filtering
 */
export const getTourEventTypes = () => {
  return {
    ...EVENT_TYPES,
    ...RESIDENCY_EVENT_TYPES
  };
};